import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFeatures } from "@/hooks/useFeatures";
import { useByocEnabled } from "@/hooks/use-byoc-enabled";
import { auth } from "@/lib/firebase";
import axios, { AxiosError } from "axios";
import { usersApi } from "@/lib/api/users";
import { workspacesApi } from "@/lib/api/workspaces";
import { workspaceKeysApi } from "@/lib/api/workspace-keys";
import {
  ProgrammingExperience,
  KeyListItem,
  WorkspaceKeyType,
  PlanType,
  SubscriptionStatus,
} from "@optidev/shared-types";
import { usePlanFeatures } from "@/hooks/useFeatures";
import { billingApi } from "@/lib/api/billing";
import toast from "react-hot-toast";
import WorkspaceKeyCard from "@/components/workspace-keys/WorkspaceKeyCard";
import GenerateKeyModal from "@/components/workspace-keys/GenerateKeyModal";
import RotateKeyModal from "@/components/workspace-keys/RotateKeyModal";
import RevokeKeyModal from "@/components/workspace-keys/RevokeKeyModal";
import { CopyableCell } from "@/components/ui/copyable-cell";
import { BillingPage } from "./billing/BillingPage";
import { CloudBalanceCard } from "./billing/CloudBalanceCard";
import { CurrencySelect } from "./billing/CurrencySelect";
import { CloudUsageCard } from "./billing/CloudUsageCard";
import { AutoReplenishCard } from "./billing/AutoReplenishCard";
import { AddFundsModal } from "./billing/AddFundsModal";
import { useCheckoutSuccessListener } from "../../hooks/useCheckoutSuccessListener";
import { NotificationSettings } from "./notification/NotificationSettings";
import { ByocCard } from "@/components/byoc";
import {
  User,
  Shield,
  CreditCard,
  Key,
  Lock,
  Mail,
  AlertCircle,
  BarChart3,
  CheckCircle,
  Link2,
  Eye,
  EyeOff,
  Loader2,
  Monitor,
  Building2,
  Github,
  Gitlab,
  Trash2,
  Plus,
  Lightbulb,
  ExternalLink,
  Users,
  UserPlus,
  Palette,
  Brain,
  Sparkles,
  Zap,
  X,
  Pencil,
  Crown,
  ChevronDown,
  Megaphone,
} from "lucide-react";
import {
  getModelConfig,
  getDefaultModel,
  isCodexModelKey,
  type ModelType,
} from "@/components/agent/ModelDropdown";
import { useCodexEnabled } from "@/hooks/use-codex-enabled";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { SAMLSettings } from "./settings/SAMLSettings";
import { ThemeLibrary } from "./settings/theme-library";
import { WorkspaceBroadcastsPage } from "@/components/broadcasts/workspace-broadcasts-page";
import { githubApi, GitHubConnection } from "@/lib/api/github";
import { gitlabApi, GitLabConnection } from "@/lib/api/gitlab";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MembersList } from "@/components/workspace-members";
import { InviteMemberDialog } from "@/components/workspace-members/invite-member-dialog";
import { usePageBar } from "@/components/layout/page-bar";
import { useWorkspace } from "@/contexts/workspace-context";
import { usePermissions } from "@/hooks/usePermissions";
import { useResponsive } from "@/hooks/useResponsive";
import { useEmbedStore } from "@/stores/embed-store";
import { cn } from "@/lib/utils";
import { openOAuthWithRefresh } from "@/lib/connector-oauth";

function CrossProjectSharingToggle({
  activeWorkspace,
}: {
  activeWorkspace: { id: string; metadata?: Record<string, any> } | null;
}) {
  const [enabled, setEnabled] = useState(
    activeWorkspace?.metadata?.crossProjectSharingEnabled !== false,
  );

  return (
    <Switch
      checked={enabled}
      onCheckedChange={async (checked) => {
        setEnabled(checked); // Instant UI update
        try {
          const token = await auth.getIdToken();
          if (!token || !activeWorkspace?.id) return;
          await workspacesApi.updateWorkspace(token, activeWorkspace.id, {
            metadata: {
              ...activeWorkspace.metadata,
              crossProjectSharingEnabled: checked,
            },
          });
          toast.success(
            checked
              ? "Cross-project sharing enabled"
              : "Cross-project sharing disabled",
          );
        } catch {
          setEnabled(!checked); // Revert on failure
          toast.error("Failed to update sharing settings");
        }
      }}
    />
  );
}

/** Muted one-line description shown under each Settings tab title in the page header. */
const SETTINGS_TAB_DESCRIPTIONS: Record<string, string> = {
  profile: "Manage your personal account details.",
  members: "Invite teammates and manage their roles.",
  workspace: "Configure your workspace and cross-project sharing.",
  security: "Manage sign-in and security preferences.",
  saml: "Configure enterprise single sign-on for your organization.",
  "cloud-usage": "Track your cloud resource usage.",
  github: "Connect a GitHub account to import and sync repositories.",
  gitlab: "Connect a GitLab account to import and sync repositories.",
  "api-keys": "Create and manage API keys for your workspace.",
  "theme-library": "Browse and manage themes for your projects.",
  broadcasts: "Schedule dashboard captures and deliver them to Slack, Teams, email, webhook, or PDF.",
  notifications: "Choose which notifications you receive.",
  integration: "Manage third-party integrations.",
};

export default function SettingsFull() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tab } = useParams<{ tab: string }>();
  const { features, loading: featuresLoading } = useFeatures();
  const { enabled: byocEnabled } = useByocEnabled();
  const { enabled: codexEnabled } = useCodexEnabled();
  const { activeWorkspace, workspaces, refreshWorkspaces } = useWorkspace();
  const {
    canInviteMembers,
    canManageWorkspace,
    canEditSettings,
    isAdminOrOwner,
  } = usePermissions();
  const { isMobile } = useResponsive();
  const isEmbedded = useEmbedStore((s) => s.isEmbedded);

  // Valid tab IDs (filtered by feature flags and permissions)
  const validTabs = useMemo(() => {
    const allTabs = [
      "profile",
      "members",
      "workspace",
      "security",
      "saml",
      "notifications",
      "billing",
      "cloud-usage",
      "integration",
      "github",
      "gitlab",
      "api-keys",
      "theme-library",
      "broadcasts",
    ];
    return allTabs.filter((t) => {
      if (t === "notifications") return false;
      if (t === "integration") return false;
      if (t === "broadcasts" && (!features.broadcast || !isAdminOrOwner))
        return false;
      if (t === "workspace" && (!features.settingsWorkspace || !isAdminOrOwner))
        return false;
      if (t === "billing" && !isAdminOrOwner) return false;
      if (t === "cloud-usage" && !isAdminOrOwner) return false;
      if (t === "saml" && !isAdminOrOwner) return false;
      if (t === "api-keys" && !isAdminOrOwner) return false;
      if (t === "github" && !isAdminOrOwner) return false;
      if (t === "gitlab" && !isAdminOrOwner) return false;
      if (
        isEmbedded &&
        [
          "profile",
          "members",
          "security",
          "saml",
          "notifications",
          "integration",
        ].includes(t)
      )
        return false;
      return true;
    });
  }, [features, isAdminOrOwner, isEmbedded]);

  // Current active tab with validation - default to profile (accessible by all)
  // Permanent alias: /settings/reports predates the Broadcast rename and is stamped into
  // engine-stored linkBack configs and already-delivered emails/Slack messages — those links
  // must land here forever, so the old slug maps to the canonical tab instead of 404-ing
  // to profile. (Kept as a mapping, not a <Navigate>, so the address bar keeps working URLs.)
  const canonicalTab = tab === "reports" ? "broadcasts" : tab;
  const activeTab = validTabs.includes(canonicalTab || "") ? canonicalTab! : "profile";
  const [showOptiSignsKey, setShowOptiSignsKey] = useState(false);
  const [optiSignsKey, setOptiSignsKey] = useState("");
  const [originalOptiSignsKey, setOriginalOptiSignsKey] = useState("");
  const [loadingIntegration, setLoadingIntegration] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectedScreens, setConnectedScreens] = useState<any[]>([]);
  const [integrationMessage, setIntegrationMessage] = useState<{
    type: "success" | "error" | "info" | null;
    text: string;
  }>({ type: null, text: "" });
  const [authInitialized, setAuthInitialized] = useState(false);

  // GitHub connections state
  const [githubConnections, setGithubConnections] = useState<
    GitHubConnection[]
  >([]);
  const [loadingGithub, setLoadingGithub] = useState(false);
  const [deletingConnectionId, setDeletingConnectionId] = useState<
    string | null
  >(null);

  // GitLab connections state
  const [gitlabConnections, setGitlabConnections] = useState<
    GitLabConnection[]
  >([]);
  const [loadingGitlab, setLoadingGitlab] = useState(false);
  const [deletingGitlabConnectionId, setDeletingGitlabConnectionId] = useState<
    string | null
  >(null);

  // Workspace Keys state
  const [workspaceKeys, setWorkspaceKeys] = useState<KeyListItem[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [generateModal, setGenerateModal] = useState<{
    open: boolean;
    keyType: WorkspaceKeyType;
  }>({ open: false, keyType: WorkspaceKeyType.CONTENT });
  const [rotateModal, setRotateModal] = useState<{
    open: boolean;
    keyId?: string;
    version?: number;
  }>({ open: false });
  const [revokeModal, setRevokeModal] = useState<{
    open: boolean;
    keyId?: string;
  }>({ open: false });

  // Cloud Usage state
  const [addFundsModalOpen, setAddFundsModalOpen] = useState(false);
  const [cloudRefreshKey, setCloudRefreshKey] = useState(0);

  // Embed cloud-add-funds: Stripe success tab broadcasts to this page, refresh balance + toast.
  useCheckoutSuccessListener("cloud-add-funds", () => {
    setCloudRefreshKey((k) => k + 1);
    setAddFundsModalOpen(false);
    toast.success("Payment successful. Cloud balance updated.");
  });

  // Profile state
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    role: "",
    department: "",
    programmingExperience: ProgrammingExperience.BEGINNER,
    usageIntent: "",
  });

  // Workspace state
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [savingWorkspace, setSavingWorkspace] = useState(false);
  const [workspaceData, setWorkspaceData] = useState({
    industry: "",
    size: "",
    locations: [] as string[],
    description: "",
    primaryUseCase: "",
  });
  const [locationInput, setLocationInput] = useState("");

  // Workspace name state
  const [workspaceName, setWorkspaceName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [memberLimit, setMemberLimit] = useState<number | null>(null);
  const [memberCount, setMemberCount] = useState(0);

  // Chat settings state
  const [shareAiChatHistory, setShareAiChatHistory] = useState(false);
  const [savingChatSettings, setSavingChatSettings] = useState(false);

  // Model settings state
  const [defaultModel, setDefaultModel] = useState<ModelType | null>(null);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const [savingModelSettings, setSavingModelSettings] = useState(false);
  const [planType, setPlanType] = useState<PlanType>(PlanType.FREE);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const { planFeatures } = usePlanFeatures();

  // Derive effective plan. Embed workspaces split into embed_trial vs
  // embed_active based on subscription.status. All other plans use planType.
  const effectivePlan: string =
    planType === PlanType.EMBED
      ? subscriptionStatus === SubscriptionStatus.TRIALING
        ? "embed_trial"
        : "embed_active"
      : planType;

  // Check if a model is allowed for the current plan.
  // Uses the `planTier` field on each ModelOption (haiku / sonnet / opus),
  // so this covers Claude and OpenRouter models alike — opus-tier models
  // (e.g. Claude Opus 5.5) are gated to Pro/Enterprise. Matches ModelDropdown.tsx.
  const isModelAllowed = (modelKey: ModelType): boolean => {
    const lookup = planFeatures as unknown as Record<
      string,
      { models: string[] } | undefined
    >;
    const currentPlanFeatures = lookup[effectivePlan] || planFeatures.free;
    const tier = getModelConfig()[modelKey]?.planTier;
    if (!tier) return true; // unknown model = allow (avoids false-blocking)
    return currentPlanFeatures.models.includes(tier);
  };

  const API_URL = import.meta.env.VITE_API_URL;
  const AUTH_URL = import.meta.env.VITE_AUTH_URL;

  // Wait for Firebase auth to initialize
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(() => {
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, []);

  // Close the workspace default-model dropdown on outside click
  useEffect(() => {
    if (!modelDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modelDropdownRef.current &&
        !modelDropdownRef.current.contains(event.target as Node)
      ) {
        setModelDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [modelDropdownOpen]);

  // Show scrollbar on settings page
  useEffect(() => {
    document.documentElement.classList.add("show-scrollbar");
    return () => {
      document.documentElement.classList.remove("show-scrollbar");
    };
  }, []);

  // Redirect to default tab if invalid tab is provided
  // Wait for activeWorkspace to load before redirecting (permissions depend on it), and for
  // the feature flags too: `features` starts with every flag off, so a cold deep link to a
  // flag-gated tab (/settings/broadcasts, /settings/workspace) was judged invalid and bounced
  // to Profile before the flags had even been fetched.
  useEffect(() => {
    if (!activeWorkspace || featuresLoading) return;
    if (tab && !validTabs.includes(tab)) {
      navigate("/dashboard/settings/profile", { replace: true });
    }
  }, [tab, navigate, validTabs, activeWorkspace, featuresLoading]);

  // Load integration settings
  useEffect(() => {
    if (activeTab === "integration") {
      loadIntegrationSettings();
    }
  }, [activeTab]);

  // Load GitHub connections
  useEffect(() => {
    if (activeTab === "github") {
      loadGitHubConnections();
    }
  }, [activeTab]);

  // Load GitLab connections
  useEffect(() => {
    if (activeTab === "gitlab") {
      loadGitLabConnections();
    }
  }, [activeTab]);

  // Auto-redirect to OptiPrime security page when security tab is active
  // Wait for auth to initialize first to avoid triggering OAuth redirect on return
  useEffect(() => {
    if (activeTab === "security" && authInitialized) {
      // Get the previous tab from location state, or use referrer/default
      const fromTab = (location.state as any)?.fromTab;

      let returnUrl: string;
      if (fromTab && fromTab !== "security") {
        // Return to the tab they came from
        returnUrl = `${window.location.origin}/dashboard/settings/${fromTab}`;
      } else {
        // Default to settings page if accessed directly
        returnUrl = `${window.location.origin}/dashboard/settings`;
      }

      // Redirect to OptiPrime security page with return URL
      window.location.href = `${AUTH_URL}/security?return_url=${encodeURIComponent(returnUrl)}`;
    }
  }, [activeTab, authInitialized, AUTH_URL, location.state]);

  // Load profile settings
  useEffect(() => {
    if (activeTab === "profile") {
      loadProfileSettings();
    }
  }, [activeTab]);

  // Load workspace settings
  useEffect(() => {
    if (activeTab === "workspace") {
      loadWorkspaceSettings();
      // Set workspace name from context
      if (activeWorkspace) {
        setWorkspaceName(activeWorkspace.name);
        setMemberCount(activeWorkspace.memberCount);
      }
      // Fetch subscription plan for model restrictions
      const fetchPlan = async () => {
        try {
          const token = await auth.getIdToken();
          if (token) {
            const subscription = await billingApi.getSubscription(token);
            setPlanType(subscription.planType);
            setSubscriptionStatus(subscription.status);
          }
        } catch (error) {
          console.error("Failed to fetch subscription:", error);
          setPlanType(PlanType.FREE);
          setSubscriptionStatus(null);
        }
      };
      fetchPlan();
    }
  }, [activeTab, activeWorkspace]);

  // Load workspace keys (admin/owner only)
  useEffect(() => {
    if (activeTab === "api-keys" && isAdminOrOwner) {
      loadWorkspaceKeys();
    }
  }, [activeTab, isAdminOrOwner]);

  const loadProfileSettings = async () => {
    setLoadingProfile(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const profile = await usersApi.getProfile(token);
      if (profile) {
        setProfileData({
          role: profile.role || "",
          department: profile.department || "",
          programmingExperience:
            profile.programmingExperience || ProgrammingExperience.BEGINNER,
          usageIntent: profile.usageIntent || "",
        });
      }
    } catch (error) {
      console.error("Failed to load profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoadingProfile(false);
    }
  };

  const saveProfileSettings = async () => {
    setSavingProfile(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      await usersApi.updateProfile(token, profileData);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Failed to save profile:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to save profile",
      );
    } finally {
      setSavingProfile(false);
    }
  };

  const loadWorkspaceSettings = async () => {
    setLoadingWorkspace(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      // Use active workspace (from URL/context), not user's default workspace
      const workspaceId = activeWorkspace?.id;
      if (!workspaceId) return;

      // Fetch workspace context, chat settings, and model settings in parallel
      const [workspaceResponse, chatSettingsResponse, modelSettingsResponse] =
        await Promise.all([
          axios.get(`${API_URL}/api/workspaces/${workspaceId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          workspacesApi.getChatSettings(token, workspaceId),
          workspacesApi.getModelSettings(token, workspaceId),
        ]);

      const workspace = workspaceResponse.data;
      if (workspace.context) {
        setWorkspaceData({
          industry: workspace.context.industry || "",
          size: workspace.context.size || "",
          locations: workspace.context.locations || [],
          description: workspace.context.description || "",
          primaryUseCase: workspace.context.primaryUseCase || "",
        });
      }

      // Set chat settings
      setShareAiChatHistory(chatSettingsResponse.shareAiChatHistory);

      // Set model settings
      setDefaultModel(modelSettingsResponse.defaultModel);
    } catch (error) {
      console.error("Failed to load workspace:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      if (axiosError.response?.status === 403) {
        toast.error("You don't have permission to edit workspace settings");
      } else {
        toast.error("Failed to load workspace settings");
      }
    } finally {
      setLoadingWorkspace(false);
    }
  };

  const saveWorkspaceSettings = async () => {
    setSavingWorkspace(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const workspaceId = activeWorkspace?.id;
      if (!workspaceId) return;

      await workspacesApi.updateContext(token, workspaceId, workspaceData);
      toast.success("Workspace updated successfully");
    } catch (error) {
      console.error("Failed to save workspace:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to save workspace",
      );
    } finally {
      setSavingWorkspace(false);
    }
  };

  const saveWorkspaceName = async () => {
    if (!workspaceName.trim()) {
      toast.error("Workspace name is required");
      return;
    }
    if (workspaceName === activeWorkspace?.name) {
      return; // No change
    }
    setSavingName(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const workspaceId = activeWorkspace?.id;
      if (!workspaceId) return;

      await workspacesApi.updateWorkspace(token, workspaceId, {
        name: workspaceName.trim(),
      });
      toast.success("Workspace name updated");
      await refreshWorkspaces();
      setEditingName(false);
    } catch (error) {
      console.error("Failed to save workspace name:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to save workspace name",
      );
    } finally {
      setSavingName(false);
    }
  };

  const handleChatSettingsChange = async (enabled: boolean) => {
    setSavingChatSettings(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const workspaceId = activeWorkspace?.id;
      if (!workspaceId) return;

      await workspacesApi.updateChatSettings(token, workspaceId, {
        shareAiChatHistory: enabled,
      });
      setShareAiChatHistory(enabled);
      toast.success(
        enabled
          ? "Team members can now view all chat sessions"
          : "Chat sessions are now private to creators",
      );
    } catch (error) {
      console.error("Failed to update chat settings:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to update chat settings",
      );
    } finally {
      setSavingChatSettings(false);
    }
  };

  const handleModelSettingsChange = async (model: ModelType | null) => {
    setSavingModelSettings(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const workspaceId = activeWorkspace?.id;
      if (!workspaceId) return;

      await workspacesApi.updateModelSettings(token, workspaceId, {
        defaultModel: model,
      });
      setDefaultModel(model);

      // Label from the shared catalog so OpenRouter/Codex picks never read "undefined".
      const modelLabel = getModelConfig()[model as string]?.modelName ?? model;

      toast.success(
        model
          ? `Default model set to ${modelLabel}`
          : "Default model cleared - will use system default",
      );
    } catch (error) {
      console.error("Failed to update model settings:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to update model settings",
      );
    } finally {
      setSavingModelSettings(false);
    }
  };

  const handleInviteSuccess = () => {
    // Refresh workspaces to update member count
    refreshWorkspaces();
  };

  const addLocation = () => {
    if (locationInput.trim()) {
      setWorkspaceData({
        ...workspaceData,
        locations: [...workspaceData.locations, locationInput.trim()],
      });
      setLocationInput("");
    }
  };

  const removeLocation = (index: number) => {
    setWorkspaceData({
      ...workspaceData,
      locations: workspaceData.locations.filter((_, i) => i !== index),
    });
  };

  const loadWorkspaceKeys = async () => {
    setLoadingKeys(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const keys = await workspaceKeysApi.list(token);
      setWorkspaceKeys(keys);
    } catch (error) {
      console.error("Failed to load workspace keys:", error);
      toast.error("Failed to load API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  const loadIntegrationSettings = async () => {
    setLoadingIntegration(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/api/integrations/optisigns`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.hasApiKey) {
        setOptiSignsKey(response.data.maskedApiKey || "");
        setOriginalOptiSignsKey(response.data.maskedApiKey || "");
        setConnectedScreens(response.data.connectedScreens || []);
      }
    } catch (error) {
      console.error("Failed to load integration settings:", error);
      setIntegrationMessage({
        type: "error",
        text: "Failed to load integration settings",
      });
    } finally {
      setLoadingIntegration(false);
    }
  };

  const loadGitHubConnections = async () => {
    setLoadingGithub(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;
      const connections = await githubApi.listConnections(token);
      setGithubConnections(connections);
    } catch (error) {
      console.error("Failed to load GitHub connections:", error);
    } finally {
      setLoadingGithub(false);
    }
  };

  const handleConnectGitHub = async () => {
    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error("Please sign in to continue");
        return;
      }
      // Pass returnPath so we redirect back here after OAuth
      const { authUrl } = await githubApi.initiateOAuth(
        token,
        undefined,
        undefined,
        "/connections/oauth/callback",
      );
      const prevGithubIds = new Set(githubConnections.map((c) => c.id));
      openOAuthWithRefresh(authUrl, loadGitHubConnections, {
        fallbackCheck: async () => {
          const t = await auth.getIdToken();
          if (!t) return null;
          const data = await githubApi.listConnections(t);
          const newConn = data.find((c) => !prevGithubIds.has(c.id));
          return newConn ? { success: true, connectionId: newConn.id } : null;
        },
      });
    } catch (error) {
      console.error("Failed to initiate GitHub OAuth:", error);
      toast.error("Failed to connect GitHub");
    }
  };

  const handleRemoveGitHubConnection = async (connectionId: string) => {
    setDeletingConnectionId(connectionId);
    try {
      const token = await auth.getIdToken();
      if (!token) return;
      await githubApi.deleteConnection(token, connectionId);
      setGithubConnections((prev) => prev.filter((c) => c.id !== connectionId));
      toast.success("GitHub connection removed");
    } catch (error) {
      console.error("Failed to remove GitHub connection:", error);
      toast.error("Failed to remove connection");
    } finally {
      setDeletingConnectionId(null);
    }
  };

  const handleInstallApp = async (connectionId: string) => {
    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error("Please sign in to continue");
        return;
      }
      // Pass current path so we redirect back here after installation
      const { url } = await githubApi.getInstallAppUrl(
        token,
        connectionId,
        "/connections/oauth/callback",
      );
      openOAuthWithRefresh(url, loadGitHubConnections, {
        fallbackCheck: async () => ({ success: true }),
      });
    } catch (error) {
      console.error("Failed to get install app URL:", error);
      toast.error("Failed to open GitHub App installation");
    }
  };

  const handleManageInstallation = async (connectionId: string) => {
    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error("Please sign in to continue");
        return;
      }
      const { url } = await githubApi.getManageInstallationUrl(
        token,
        connectionId,
      );
      openOAuthWithRefresh(url, loadGitHubConnections, {
        fallbackCheck: async () => ({ success: true }),
      });
    } catch (error) {
      console.error("Failed to get manage installation URL:", error);
      toast.error("Failed to open GitHub App settings");
    }
  };

  // GitLab connection management
  const loadGitLabConnections = async () => {
    setLoadingGitlab(true);
    try {
      const token = await auth.getIdToken();
      if (!token) return;
      const connections = await gitlabApi.listConnections(token);
      setGitlabConnections(connections);
    } catch (error) {
      console.error("Failed to load GitLab connections:", error);
    } finally {
      setLoadingGitlab(false);
    }
  };

  const handleConnectGitLab = async () => {
    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error("Please sign in to continue");
        return;
      }
      const { authUrl } = await gitlabApi.initiateOAuth(
        token,
        undefined,
        undefined,
        "/connections/oauth/callback",
      );
      const prevGitlabIds = new Set(gitlabConnections.map((c) => c.id));
      openOAuthWithRefresh(authUrl, loadGitLabConnections, {
        fallbackCheck: async () => {
          const t = await auth.getIdToken();
          if (!t) return null;
          const data = await gitlabApi.listConnections(t);
          const newConn = data.find((c) => !prevGitlabIds.has(c.id));
          return newConn ? { success: true, connectionId: newConn.id } : null;
        },
      });
    } catch (error) {
      console.error("Failed to initiate GitLab OAuth:", error);
      toast.error("Failed to connect GitLab");
    }
  };

  const handleRemoveGitLabConnection = async (connectionId: string) => {
    setDeletingGitlabConnectionId(connectionId);
    try {
      const token = await auth.getIdToken();
      if (!token) return;
      await gitlabApi.deleteConnection(token, connectionId);
      setGitlabConnections((prev) => prev.filter((c) => c.id !== connectionId));
      toast.success("GitLab connection removed");
    } catch (error) {
      console.error("Failed to remove GitLab connection:", error);
      toast.error("Failed to remove connection");
    } finally {
      setDeletingGitlabConnectionId(null);
    }
  };

  const saveOptiSignsKey = async () => {
    if (!optiSignsKey || optiSignsKey === originalOptiSignsKey) return;

    setSavingKey(true);
    setIntegrationMessage({ type: null, text: "" });

    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const response = await axios.put(
        `${API_URL}/api/integrations/optisigns`,
        { apiKey: optiSignsKey },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setIntegrationMessage({
          type: "success",
          text: "API key saved successfully",
        });
        setOriginalOptiSignsKey(optiSignsKey);
        // Auto-test the connection after saving
        setTimeout(() => testConnection(), 500);
      }
    } catch (error) {
      console.error("Failed to save API key:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to save API key";
      setIntegrationMessage({ type: "error", text: message });
    } finally {
      setSavingKey(false);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setIntegrationMessage({ type: null, text: "" });

    try {
      const token = await auth.getIdToken();
      if (!token) return;

      const response = await axios.post(
        `${API_URL}/api/integrations/optisigns/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success) {
        setIntegrationMessage({ type: "success", text: response.data.message });
        if (response.data.screens) {
          setConnectedScreens(response.data.screens);
        }
      } else {
        setIntegrationMessage({ type: "error", text: response.data.message });
      }
    } catch (error) {
      console.error("Connection test failed:", error);
      const axiosError = error as AxiosError<{ message?: string }>;
      const message =
        axiosError.response?.data?.message || "Connection test failed";
      setIntegrationMessage({ type: "error", text: message });
    } finally {
      setTestingConnection(false);
    }
  };

  const copyApiKey = async () => {
    if (!originalOptiSignsKey) return;

    // For security, we don't copy the masked key
    navigator.clipboard.writeText("API_KEY_HIDDEN_FOR_SECURITY");
    setIntegrationMessage({
      type: "info",
      text: "API key copied (masked for security)",
    });
    setTimeout(() => setIntegrationMessage({ type: null, text: "" }), 3000);
  };

  // Filter tabs by feature flags + include members tab
  const tabs = useMemo(() => {
    const allTabs = [
      { id: "profile", name: "Profile", icon: User },
      { id: "members", name: "Members", icon: Users },
      ...(isAdminOrOwner && features.settingsWorkspace
        ? [{ id: "workspace", name: "Workspace", icon: Building2 }]
        : []),
      { id: "security", name: "Security", icon: Shield },
      ...(isAdminOrOwner ? [{ id: "saml", name: "SAML SSO", icon: Lock }] : []),
      ...(isAdminOrOwner
        ? [{ id: "billing", name: "Billing", icon: CreditCard }]
        : []),
      ...(isAdminOrOwner
        ? [{ id: "cloud-usage", name: "Cloud Usage", icon: BarChart3 }]
        : []),
      ...(isAdminOrOwner
        ? [{ id: "github", name: "GitHub", icon: Github }]
        : []),
      ...(isAdminOrOwner
        ? [{ id: "gitlab", name: "GitLab", icon: Gitlab }]
        : []),
      ...(isAdminOrOwner
        ? [{ id: "api-keys", name: "API Keys", icon: Key }]
        : []),
      { id: "theme-library", name: "Theme Library", icon: Palette },
      ...(isAdminOrOwner && features.broadcast
        ? [{ id: "broadcasts", name: "Broadcast", icon: Megaphone }]
        : []),
    ];
    const embeddedHidden = [
      "profile",
      "members",
      "security",
      "saml",
      "notifications",
      "integration",
    ];
    return isEmbedded
      ? allTabs.filter((t) => !embeddedHidden.includes(t.id))
      : allTabs;
  }, [isAdminOrOwner, features, isEmbedded]);

  // Shell page bar: "Settings / <active tab>". The Invite action stays inside
  // MembersList (it lives there today — no duplication in the bar).
  const activeTabName =
    tabs.find((tabItem) => tabItem.id === activeTab)?.name ?? "Settings";
  usePageBar({
    title: activeTabName,
    description: SETTINGS_TAB_DESCRIPTIONS[activeTab],
  });

  return (
    <div className="min-h-full">
      <div
        className={cn(
          "w-full",
          isMobile ? "px-4 py-6" : "px-6 sm:px-8 lg:px-10 pt-6 pb-10",
        )}
      >
        {/* Mobile: Horizontal scrollable tabs - full width */}
        {isMobile && (
          <div className="mb-4 -mx-4 w-screen">
            <div className="flex gap-2 overflow-x-auto px-4 pb-2 scrollbar-hide">
              {tabs.map((tabItem) => (
                <button
                  key={tabItem.id}
                  onClick={() =>
                    navigate(`/dashboard/settings/${tabItem.id}`, {
                      state: { fromTab: activeTab },
                    })
                  }
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors flex-shrink-0",
                    activeTab === tabItem.id
                      ? "bg-accent dark:bg-white/[0.08] text-foreground border border-border/50 dark:border-transparent"
                      : "text-muted-foreground hover:bg-accent/50 dark:hover:bg-white/[0.04] hover:text-foreground border border-transparent",
                  )}
                >
                  <tabItem.icon
                    className={cn(
                      "h-4 w-4 flex-shrink-0",
                      activeTab === tabItem.id && "text-foreground",
                    )}
                  />
                  {tabItem.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div
          className={cn(
            isMobile ? "space-y-4" : "grid grid-cols-1 lg:grid-cols-4 gap-8",
          )}
        >
          {/* Desktop: Sidebar */}
          {!isMobile && (
            <div className="lg:col-span-1">
              <Card className="border border-neutral-100 bg-neutral-50 dark:border-neutral-800 dark:bg-muted shadow-none">
                <CardContent className="p-2">
                  <nav className="space-y-1">
                    {tabs.map((tabItem) => (
                      <button
                        key={tabItem.id}
                        onClick={() =>
                          navigate(`/dashboard/settings/${tabItem.id}`, {
                            state: { fromTab: activeTab },
                          })
                        }
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activeTab === tabItem.id
                            ? "bg-accent dark:bg-white/[0.08] text-foreground border border-border/50 dark:border-transparent"
                            : "text-muted-foreground hover:bg-accent/50 dark:hover:bg-white/[0.04] hover:text-foreground border border-transparent"
                        } ${"indent" in tabItem && tabItem.indent ? "ml-4" : ""}`}
                      >
                        <tabItem.icon
                          className={`h-4 w-4 mr-3 ${
                            activeTab === tabItem.id ? "text-foreground" : ""
                          }`}
                        />
                        {tabItem.name}
                      </button>
                    ))}
                  </nav>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Content */}
          <div className={cn(!isMobile && "lg:col-span-3")}>
            {/* Mobile: card with less padding. Desktop: card with more padding */}
            <div
              className={cn(
                "space-y-6 rounded-xl bg-card border border-neutral-100 dark:border-neutral-800",
                isMobile ? "p-4" : "p-6 lg:p-8",
              )}
            >
              {activeTab === "profile" && loadingProfile && (
                <div className="flex justify-center items-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}

              {activeTab === "profile" && !loadingProfile && (
                <div className="space-y-6">
                  {/* Header Section */}
                  <div className="flex flex-col gap-3 pb-5 border-b border-border/40">
                    <div
                      className={cn(
                        "flex gap-4",
                        isMobile ? "flex-col" : "items-center justify-between",
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground font-semibold text-lg">
                          {user?.email?.charAt(0)?.toUpperCase() || "U"}
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold">
                            {user?.email}
                          </h2>
                          <p className="text-xs text-muted-foreground">
                            {profileData.role || "No role set"}{" "}
                            {profileData.department
                              ? `• ${profileData.department}`
                              : ""}
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={saveProfileSettings}
                        disabled={savingProfile}
                        variant="ghost"
                        className="text-xs h-8 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                      >
                        {savingProfile ? (
                          <>
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          "Save Changes"
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Main Content Grid */}
                  <div
                    className={cn(
                      "grid gap-6",
                      isMobile ? "grid-cols-1" : "grid-cols-2",
                    )}
                  >
                    {/* Left Column - Work Info */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Work Information
                      </h3>

                      <Card className="border border-neutral-100 bg-neutral-50 dark:border-neutral-800 dark:bg-muted shadow-none">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-start gap-3 mb-1">
                            <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                              <User className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div>
                              <h4 className="text-sm font-medium">Your Role</h4>
                              <p className="text-xs text-muted-foreground">
                                Help AI understand your context
                              </p>
                            </div>
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="role-title" className="text-xs">
                              Job Title
                            </Label>
                            <Input
                              id="role-title"
                              value={profileData.role}
                              onChange={(e) =>
                                setProfileData({
                                  ...profileData,
                                  role: e.target.value,
                                })
                              }
                              placeholder="e.g., IT Admin Manager"
                              className="h-8 text-sm"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="department" className="text-xs">
                              Department (optional)
                            </Label>
                            <Input
                              id="department"
                              value={profileData.department}
                              onChange={(e) =>
                                setProfileData({
                                  ...profileData,
                                  department: e.target.value,
                                })
                              }
                              placeholder="e.g., Plant Operations"
                              className="h-8 text-sm"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label htmlFor="usage-intent" className="text-xs">
                              How will you use OptiDev?
                            </Label>
                            <textarea
                              id="usage-intent"
                              value={profileData.usageIntent}
                              onChange={(e) =>
                                setProfileData({
                                  ...profileData,
                                  usageIntent: e.target.value,
                                })
                              }
                              placeholder="e.g., Build dashboards for production monitoring"
                              rows={2}
                              className="w-full px-3 py-1.5 text-sm border border-neutral-100 dark:border-neutral-800 rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-neutral-400 dark:focus:ring-neutral-600 resize-none"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Right Column - Experience Level */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Experience Level
                      </h3>

                      <Card className="border border-neutral-100 bg-neutral-50 dark:border-neutral-800 dark:bg-muted shadow-none">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                              <Monitor className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div>
                              <h4 className="text-sm font-medium">
                                Programming Experience
                              </h4>
                              <p className="text-xs text-muted-foreground">
                                AI adapts to your skill level
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            {[
                              {
                                value: ProgrammingExperience.NONE,
                                label: "None",
                                desc: "Non-technical",
                              },
                              {
                                value: ProgrammingExperience.BEGINNER,
                                label: "Beginner",
                                desc: "Basic scripting",
                              },
                              {
                                value: ProgrammingExperience.INTERMEDIATE,
                                label: "Intermediate",
                                desc: "Build features",
                              },
                              {
                                value: ProgrammingExperience.ADVANCED,
                                label: "Advanced",
                                desc: "Full-stack",
                              },
                            ].map(({ value, label, desc }) => (
                              <button
                                key={value}
                                onClick={() =>
                                  setProfileData({
                                    ...profileData,
                                    programmingExperience: value,
                                  })
                                }
                                className={cn(
                                  "relative p-2.5 rounded-lg border-2 transition-all text-left",
                                  profileData.programmingExperience === value
                                    ? "border-transparent bg-neutral-900 text-white dark:bg-white dark:text-neutral-900 [&_.text-muted-foreground]:text-neutral-400 dark:[&_.text-muted-foreground]:text-neutral-500"
                                    : "border-transparent bg-muted/40 hover:bg-muted/70",
                                )}
                              >
                                <div className="text-xs font-medium">
                                  {label}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                  {desc}
                                </div>
                              </button>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      {/* Account Info */}
                      <Card className="border border-neutral-100 bg-neutral-50 dark:border-neutral-800 dark:bg-muted shadow-none">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-sm font-medium">
                                Account Email
                              </h4>
                              <p className="text-xs text-muted-foreground">
                                {user?.email} &middot; Cannot be changed
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "members" && <MembersList />}

              {activeTab === "workspace" && loadingWorkspace && (
                <div className="flex justify-center items-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}

              {activeTab === "workspace" && !loadingWorkspace && (
                <div className="space-y-8">
                  {/* Header Section */}
                  <div className="pb-6 border-b border-border/40">
                    <div
                      className={cn(
                        "flex gap-4",
                        isMobile ? "flex-col" : "items-center justify-between",
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground font-semibold text-base">
                          {workspaceName?.charAt(0)?.toUpperCase() || "W"}
                        </div>
                        <div>
                          {canManageWorkspace && editingName ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={workspaceName}
                                onChange={(e) =>
                                  setWorkspaceName(e.target.value)
                                }
                                className="text-lg font-semibold h-9 w-[280px]"
                                autoFocus
                                disabled={savingName}
                                onKeyDown={(e) => {
                                  if (
                                    e.key === "Enter" &&
                                    workspaceName !== activeWorkspace?.name
                                  ) {
                                    saveWorkspaceName();
                                  }
                                  if (e.key === "Escape") {
                                    setWorkspaceName(
                                      activeWorkspace?.name || "",
                                    );
                                    setEditingName(false);
                                  }
                                }}
                                onBlur={() => {
                                  if (workspaceName === activeWorkspace?.name) {
                                    setEditingName(false);
                                  }
                                }}
                              />
                              {workspaceName !== activeWorkspace?.name ? (
                                <Button
                                  variant="ghost"
                                  onClick={saveWorkspaceName}
                                  disabled={savingName}
                                  className="text-xs h-8 px-2.5 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                                >
                                  {savingName ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    "Save"
                                  )}
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  onClick={() => setEditingName(false)}
                                  className="text-xs h-8 px-2.5 text-muted-foreground hover:text-foreground"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 group">
                              <h2 className="text-lg font-semibold">
                                {workspaceName}
                              </h2>
                              {canManageWorkspace && (
                                <button
                                  onClick={() => setEditingName(true)}
                                  className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          )}
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5">
                            <span className="flex items-center gap-1">
                              <Users className="h-3.5 w-3.5" />
                              {memberCount}{" "}
                              {memberCount === 1 ? "member" : "members"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {canInviteMembers && !isEmbedded && (
                          <Button
                            variant="ghost"
                            onClick={() => setInviteDialogOpen(true)}
                            className="text-xs h-8 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                          >
                            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                            Invite Member
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Main Content Grid */}
                  <div
                    className={cn(
                      "grid gap-6",
                      isMobile ? "grid-cols-1" : "grid-cols-2",
                    )}
                  >
                    {/* Left Column - AI Settings */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        AI Settings
                      </h3>

                      {/* Default AI Model */}
                      {canEditSettings && (
                        <Card className="border border-neutral-100 bg-neutral-50 dark:border-neutral-800 dark:bg-muted shadow-none">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3 mb-3">
                              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                                <Brain className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div>
                                <h4 className="text-sm font-medium">
                                  Default Model
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                  Set for projects with no model preference
                                </p>
                              </div>
                            </div>

                            {(() => {
                              const allModels = getModelConfig({ includeCodex: codexEnabled });
                              const currentKey = defaultModel || getDefaultModel();
                              const currentCfg = allModels[currentKey];
                              const groups = [
                                {
                                  label: "Anthropic",
                                  entries: Object.entries(allModels).filter(
                                    ([key]) => !key.includes("/"),
                                  ),
                                },
                                // Codex engine group (#90): only when the engine is enabled for the viewer
                                ...(codexEnabled
                                  ? [
                                      {
                                        label: "OpenAI · Codex",
                                        entries: Object.entries(allModels).filter(
                                          ([key]) => isCodexModelKey(key),
                                        ),
                                      },
                                    ]
                                  : []),
                                {
                                  label: "Other providers",
                                  entries: Object.entries(allModels).filter(
                                    ([key]) => key.includes("/") && !isCodexModelKey(key),
                                  ),
                                },
                              ];

                              const renderMeter = (value: number) => (
                                <span className="flex items-center gap-0.5">
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <span
                                      key={i}
                                      className={cn(
                                        "h-1.5 w-3 rounded-sm",
                                        i < value
                                          ? "bg-foreground/70"
                                          : "bg-foreground/15",
                                      )}
                                    />
                                  ))}
                                </span>
                              );

                              const renderCost = (tier: number) => (
                                <span className="font-mono text-[11px] tracking-tight">
                                  {Array.from({ length: 5 }).map((_, i) => (
                                    <span
                                      key={i}
                                      className={cn(
                                        i < tier
                                          ? "text-foreground/80"
                                          : "text-foreground/20",
                                      )}
                                    >
                                      $
                                    </span>
                                  ))}
                                </span>
                              );

                              return (
                                <div className="relative" ref={modelDropdownRef}>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      !savingModelSettings &&
                                      setModelDropdownOpen((v) => !v)
                                    }
                                    disabled={savingModelSettings}
                                    className={cn(
                                      "w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md",
                                      "border border-input bg-background hover:bg-muted/40",
                                      "text-sm font-medium text-foreground",
                                      "transition-colors duration-150",
                                      savingModelSettings
                                        ? "opacity-50 cursor-not-allowed"
                                        : "cursor-pointer",
                                      modelDropdownOpen && "bg-muted/40",
                                    )}
                                  >
                                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                      {currentCfg?.planTier === "opus" && (
                                        <Crown
                                          className="h-3.5 w-3.5 shrink-0 text-purple-500 dark:text-purple-300"
                                          aria-label="Premium model"
                                        />
                                      )}
                                      <span className="truncate">
                                        {currentCfg?.modelName.replace(
                                          " (Default)",
                                          "",
                                        ) || "Select model"}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {currentCfg && (
                                        <>
                                          <span
                                            className="inline-flex items-center gap-1 text-muted-foreground"
                                            title="Intelligence"
                                          >
                                            <Brain className="h-3 w-3" />
                                            {renderMeter(currentCfg.intelligence)}
                                          </span>
                                          {renderCost(currentCfg.costTier)}
                                        </>
                                      )}
                                      <ChevronDown
                                        className={cn(
                                          "w-3.5 h-3.5 text-muted-foreground transition-transform",
                                          modelDropdownOpen && "rotate-180",
                                        )}
                                      />
                                    </div>
                                  </button>

                                  {modelDropdownOpen && (
                                    <div className="absolute top-full left-0 mt-1 w-full bg-popover rounded-lg shadow-lg border border-border z-50 max-h-[480px] overflow-y-auto">
                                      {groups.map((group, idx) => (
                                        <div
                                          key={group.label}
                                          className={cn(
                                            "px-1.5",
                                            idx === 0 ? "pt-1.5 pb-0.5" : "pb-1.5",
                                          )}
                                        >
                                          <div className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                                            {group.label}
                                          </div>
                                          <div className="flex flex-col gap-1">
                                            {group.entries.map(([key, cfg]) => {
                                              const isSelected = currentKey === key;
                                              const allowed = isModelAllowed(key);
                                              const isPremium = cfg.planTier === "opus";
                                              return (
                                                <button
                                                  key={key}
                                                  type="button"
                                                  onClick={() => {
                                                    if (!allowed || savingModelSettings) return;
                                                    handleModelSettingsChange(key);
                                                    setModelDropdownOpen(false);
                                                  }}
                                                  disabled={!allowed}
                                                  className={cn(
                                                    "w-full text-left px-2.5 py-1.5 rounded-md transition-colors duration-150",
                                                    "flex items-center justify-between gap-2",
                                                    !allowed
                                                      ? "opacity-60 cursor-not-allowed"
                                                      : isSelected
                                                        ? "bg-primary/10 dark:bg-primary/20 ring-1 ring-inset ring-primary/40 dark:ring-primary/60"
                                                        : "hover:bg-foreground/[0.04] dark:hover:bg-foreground/[0.06] cursor-pointer",
                                                  )}
                                                >
                                                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                                    {isPremium && (
                                                      <Crown
                                                        className="h-3.5 w-3.5 shrink-0 text-purple-500 dark:text-purple-300"
                                                        aria-label="Premium model"
                                                      />
                                                    )}
                                                    <span
                                                      className={cn(
                                                        "text-sm font-medium truncate",
                                                        isSelected
                                                          ? "text-primary dark:text-primary-foreground"
                                                          : "text-foreground",
                                                      )}
                                                    >
                                                      {cfg.modelName.replace(" (Default)", "")}
                                                    </span>
                                                    {!allowed && (
                                                      <span className="shrink-0 text-[10px] bg-muted px-1.5 py-0.5 rounded">
                                                        Pro
                                                      </span>
                                                    )}
                                                  </div>
                                                  <div className="flex items-center gap-2 shrink-0">
                                                    <span
                                                      className="inline-flex items-center gap-1 text-muted-foreground"
                                                      title="Intelligence"
                                                    >
                                                      <Brain className="h-3 w-3" />
                                                      {renderMeter(cfg.intelligence)}
                                                    </span>
                                                    {renderCost(cfg.costTier)}
                                                  </div>
                                                </button>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </CardContent>
                        </Card>
                      )}

                      {/* Chat History Sharing */}
                      {canEditSettings && (
                        <Card className="border border-neutral-100 bg-neutral-50 dark:border-neutral-800 dark:bg-muted shadow-none">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-start gap-3">
                                <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                                  <Users className="h-4 w-4 text-muted-foreground" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium">
                                    Share Chat History
                                  </h4>
                                  <p className="text-xs text-muted-foreground">
                                    {shareAiChatHistory
                                      ? "Team can view all sessions"
                                      : "Sessions are private"}
                                  </p>
                                </div>
                              </div>
                              <Switch
                                checked={shareAiChatHistory}
                                onCheckedChange={handleChatSettingsChange}
                                disabled={savingChatSettings}
                              />
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* BYOC Card */}
                      {canEditSettings && byocEnabled && <ByocCard />}
                    </div>

                    {/* Right Column - Company Profile */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Company Profile
                      </h3>

                      {canEditSettings && (
                        <Card className="border border-neutral-100 bg-neutral-50 dark:border-neutral-800 dark:bg-muted shadow-none">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-start gap-3 mb-1">
                              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div>
                                <h4 className="text-sm font-medium">
                                  AI Context
                                </h4>
                                <p className="text-xs text-muted-foreground">
                                  Help AI understand your company
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label htmlFor="industry" className="text-xs">
                                  Industry
                                </Label>
                                <Input
                                  id="industry"
                                  value={workspaceData.industry}
                                  onChange={(e) =>
                                    setWorkspaceData({
                                      ...workspaceData,
                                      industry: e.target.value,
                                    })
                                  }
                                  placeholder="e.g., Manufacturing"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label htmlFor="size" className="text-xs">
                                  Company Size
                                </Label>
                                <Input
                                  id="size"
                                  value={workspaceData.size}
                                  onChange={(e) =>
                                    setWorkspaceData({
                                      ...workspaceData,
                                      size: e.target.value,
                                    })
                                  }
                                  placeholder="e.g., 250 employees"
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>

                            <div className="space-y-1">
                              <Label
                                htmlFor="primary-use-case"
                                className="text-xs"
                              >
                                Primary Use Case
                              </Label>
                              <Input
                                id="primary-use-case"
                                value={workspaceData.primaryUseCase}
                                onChange={(e) =>
                                  setWorkspaceData({
                                    ...workspaceData,
                                    primaryUseCase: e.target.value,
                                  })
                                }
                                placeholder="e.g., Production dashboards"
                                className="h-8 text-sm"
                              />
                            </div>

                            <div className="space-y-1">
                              <Label className="text-xs">Locations</Label>
                              <Input
                                value={locationInput}
                                onChange={(e) =>
                                  setLocationInput(e.target.value)
                                }
                                placeholder="Type and press Enter"
                                className="h-8 text-sm"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    addLocation();
                                  }
                                }}
                              />
                              {workspaceData.locations.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                  {workspaceData.locations.map(
                                    (location, index) => (
                                      <span
                                        key={index}
                                        className="inline-flex items-center pl-2 pr-1 py-0.5 text-xs bg-foreground/10 text-foreground/70 rounded-md"
                                      >
                                        {location}
                                        <button
                                          type="button"
                                          onClick={() => removeLocation(index)}
                                          className="ml-1 hover:text-foreground"
                                        >
                                          <X className="h-3 w-3" />
                                        </button>
                                      </span>
                                    ),
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="space-y-1">
                              <Label htmlFor="description" className="text-xs">
                                Description
                              </Label>
                              <textarea
                                id="description"
                                value={workspaceData.description}
                                onChange={(e) =>
                                  setWorkspaceData({
                                    ...workspaceData,
                                    description: e.target.value,
                                  })
                                }
                                placeholder="Brief description of your company..."
                                rows={2}
                                className="w-full px-3 py-1.5 text-sm border border-neutral-100 dark:border-neutral-800 rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-neutral-400 dark:focus:ring-neutral-600 resize-none"
                              />
                            </div>

                            <Button
                              onClick={saveWorkspaceSettings}
                              disabled={savingWorkspace}
                              variant="ghost"
                              className="w-full h-8 text-xs bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                            >
                              {savingWorkspace ? (
                                <>
                                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                "Save Profile"
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>

                  {/* Cross-Project Sharing (workspace level) */}
                  {canManageWorkspace && (
                    <div className="pt-5 border-t border-neutral-100/60 dark:border-neutral-800/60">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-medium">
                            Cross-project sharing
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Allow projects to read files from other projects
                          </p>
                        </div>
                        <CrossProjectSharingToggle
                          activeWorkspace={activeWorkspace}
                        />
                      </div>
                    </div>
                  )}

                  {/* Invite Member Dialog */}
                  <InviteMemberDialog
                    open={inviteDialogOpen}
                    onOpenChange={setInviteDialogOpen}
                    onSuccess={handleInviteSuccess}
                    memberLimit={memberLimit}
                    currentCount={memberCount}
                  />
                </div>
              )}

              {activeTab === "security" && (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Redirecting to Security Settings...
                    </p>
                  </div>
                </div>
              )}

              {activeTab === "notifications" && <NotificationSettings />}

              {activeTab === "broadcasts" && features.broadcast && (
                <WorkspaceBroadcastsPage />
              )}

              {activeTab === "billing" && <BillingPage />}

              {activeTab === "cloud-usage" && (
                <div className="space-y-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-bold mb-1">Cloud Usage</h2>
                      <p className="text-sm text-muted-foreground">
                        Balance and usage for OptiDev Cloud services. Your
                        monthly allowance refills on the 1st; added funds never
                        expire. Changes may take up to 24 hours to appear.
                      </p>
                    </div>
                    <CurrencySelect />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <CloudBalanceCard
                      key={cloudRefreshKey}
                      onAddFunds={() => setAddFundsModalOpen(true)}
                    />
                    <div className="flex flex-col gap-4">
                      <AutoReplenishCard />
                      <CloudUsageCard />
                    </div>
                  </div>

                  <AddFundsModal
                    isOpen={addFundsModalOpen}
                    onClose={() => setAddFundsModalOpen(false)}
                    onSuccess={() => setCloudRefreshKey((k) => k + 1)}
                  />
                </div>
              )}

              {activeTab === "integration" && loadingIntegration && (
                <div className="flex justify-center items-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}

              {activeTab === "integration" && !loadingIntegration && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold mb-1">Integration</h2>
                    <p className="text-sm text-muted-foreground">
                      Connect OptiDev with external services
                    </p>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <img
                          src="/optisigns-icon.png"
                          alt="OptiSigns"
                          className="h-5 w-5"
                        />
                        OptiSigns API Key
                      </CardTitle>
                      <CardDescription>
                        Enter your OptiSigns API key to push content directly to
                        your digital signage screens
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="optisigns-key">API Key</Label>
                        <div className="relative">
                          <Input
                            id="optisigns-key"
                            type={showOptiSignsKey ? "text" : "password"}
                            value={optiSignsKey}
                            onChange={(e) => setOptiSignsKey(e.target.value)}
                            placeholder="sk-optisigns-..."
                            className="pr-16 font-mono text-sm"
                            disabled={savingKey || testingConnection}
                          />
                          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                            {originalOptiSignsKey && (
                              <CopyableCell
                                value="API_KEY_HIDDEN_FOR_SECURITY"
                                display=""
                                tooltipLabel="Copy API key"
                                iconSize={14}
                                iconClassName="text-muted-foreground hover:text-foreground"
                                onCopy={copyApiKey}
                              />
                            )}
                            <button
                              type="button"
                              onClick={() =>
                                setShowOptiSignsKey(!showOptiSignsKey)
                              }
                              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                              disabled={!optiSignsKey}
                            >
                              {showOptiSignsKey ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Generate this key from your OptiSigns account settings
                        </p>
                      </div>

                      <Card className="border-border/40 bg-muted/50">
                        <CardContent className="pt-6">
                          <div className="flex gap-3">
                            <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                How to get your OptiSigns API key:
                              </p>
                              <ol className="text-xs text-blue-600 dark:text-blue-400 space-y-1 list-decimal list-inside">
                                <li>Log in to your OptiSigns account</li>
                                <li>Go to Settings → API Keys</li>
                                <li>Click "Generate New Key"</li>
                                <li>Copy and paste the key here</li>
                              </ol>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <div
                        className={cn(
                          "flex gap-2",
                          isMobile ? "flex-col" : "justify-start",
                        )}
                      >
                        <Button
                          onClick={saveOptiSignsKey}
                          disabled={
                            savingKey ||
                            !optiSignsKey ||
                            optiSignsKey === originalOptiSignsKey
                          }
                          className={cn(isMobile && "w-full")}
                        >
                          {savingKey ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            "Save API Key"
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={testConnection}
                          disabled={testingConnection || !originalOptiSignsKey}
                          className={cn(isMobile && "w-full")}
                        >
                          {testingConnection ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Testing...
                            </>
                          ) : (
                            "Test Connection"
                          )}
                        </Button>
                      </div>

                      {integrationMessage.type && (
                        <div
                          className={`mt-4 p-2 text-sm rounded-lg border ${
                            integrationMessage.type === "success"
                              ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200"
                              : integrationMessage.type === "error"
                                ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200"
                                : "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200"
                          }`}
                        >
                          {integrationMessage.text}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Connected Screens
                      </CardTitle>
                      <CardDescription>
                        Screens available through your OptiSigns account
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {connectedScreens.length > 0 ? (
                        <div className="space-y-2">
                          {connectedScreens.map((screen) => (
                            <div
                              key={screen.id}
                              className="p-3 bg-accent rounded-lg"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-gray-100">
                                    {screen.name}
                                  </p>
                                  {screen.location && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                      Location: {screen.location}
                                    </p>
                                  )}
                                </div>
                                <Monitor className="h-5 w-5 text-green-500" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground text-center py-8">
                          {originalOptiSignsKey
                            ? "No screens found. Please check your OptiSigns account."
                            : "No OptiSigns API key configured. Add your API key above to see connected screens."}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === "saml" && (
                <div className="space-y-6">
                  {/* Title + description now live in the shell PageBar (see
                      usePageBar above) — no in-content header to avoid a
                      duplicate "SAML SSO" heading. */}
                  <SAMLSettings />
                </div>
              )}

              {activeTab === "github" && (
                <div className="space-y-6">
                  {/* Header Section */}
                  <div className="flex flex-col gap-4 pb-6 border-b border-border/40">
                    <div
                      className={cn(
                        "flex gap-4",
                        isMobile ? "flex-col" : "items-center justify-between",
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-xl bg-muted flex items-center justify-center">
                          <Github className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold">GitHub</h2>
                          <p className="text-sm text-muted-foreground">
                            {githubConnections.length}{" "}
                            {githubConnections.length === 1
                              ? "connection"
                              : "connections"}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        onClick={handleConnectGitHub}
                        className="text-xs h-8 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        Connect Account
                      </Button>
                    </div>
                  </div>

                  {/* Connections List */}
                  {loadingGithub ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : githubConnections.length > 0 ? (
                    <div className="space-y-3">
                      {githubConnections.map((conn) => (
                        <Card
                          key={conn.id}
                          className="border-0 shadow-sm bg-muted/30"
                        >
                          <CardContent className="p-4">
                            <div
                              className={cn(
                                "flex gap-4",
                                isMobile
                                  ? "flex-col"
                                  : "items-center justify-between",
                              )}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-background">
                                  <AvatarImage
                                    src={conn.metadata?.avatar_url}
                                  />
                                  <AvatarFallback className="bg-gray-100 dark:bg-gray-800">
                                    {conn.metadata?.login?.[0]?.toUpperCase() ||
                                      "G"}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  <p className="font-medium truncate">
                                    {conn.metadata?.login || conn.name}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span>
                                      Connected{" "}
                                      {new Date(
                                        conn.createdAt,
                                      ).toLocaleDateString()}
                                    </span>
                                    {conn.metadata?.hasInstallation && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                                        <CheckCircle className="h-3 w-3" />
                                        App installed
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div
                                className={cn(
                                  "flex items-center gap-2",
                                  isMobile && "justify-end",
                                )}
                              >
                                {conn.metadata?.hasInstallation ? (
                                  <Button
                                    variant="ghost"
                                    onClick={() =>
                                      handleManageInstallation(conn.id)
                                    }
                                    className="text-xs h-7 px-2.5 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1.5" />
                                    Manage
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    onClick={() => handleInstallApp(conn.id)}
                                    className="text-xs h-7 px-2.5 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                                  >
                                    Install App
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    handleRemoveGitHubConnection(conn.id)
                                  }
                                  disabled={deletingConnectionId === conn.id}
                                  className="text-muted-foreground hover:text-destructive"
                                >
                                  {deletingConnectionId === conn.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-dashed border-2 border-border/50">
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                          <Github className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <h3 className="font-medium mb-1">
                          No GitHub accounts connected
                        </h3>
                        <p className="text-sm text-muted-foreground text-center mb-4">
                          Connect your GitHub account to sync projects with
                          repositories
                        </p>
                        <Button
                          variant="ghost"
                          onClick={handleConnectGitHub}
                          className="text-xs h-8 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1.5" />
                          Connect GitHub Account
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* Tip Card */}
                  <Card className="border-0 shadow-sm bg-muted/50">
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <Lightbulb className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">Pro tip</p>
                          <p className="text-sm text-muted-foreground">
                            Use "Install App on Org" for persistent organization
                            access. Connections stay active even if team members
                            leave. Requires Organization Owner or Admin role.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === "gitlab" && (
                <div className="space-y-6">
                  {/* Header Section */}
                  <div className="flex flex-col gap-4 pb-6 border-b border-border/40">
                    <div
                      className={cn(
                        "flex gap-4",
                        isMobile ? "flex-col" : "items-center justify-between",
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-xl bg-muted flex items-center justify-center">
                          <Gitlab className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold">GitLab</h2>
                          <p className="text-sm text-muted-foreground">
                            {gitlabConnections.length}{" "}
                            {gitlabConnections.length === 1
                              ? "connection"
                              : "connections"}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        onClick={handleConnectGitLab}
                        className="text-xs h-8 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        Connect Account
                      </Button>
                    </div>
                  </div>

                  {/* Connections List */}
                  {loadingGitlab ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : gitlabConnections.length > 0 ? (
                    <div className="space-y-3">
                      {gitlabConnections.map((conn) => (
                        <Card
                          key={conn.id}
                          className="border-0 shadow-sm bg-muted/30"
                        >
                          <CardContent className="p-4">
                            <div
                              className={cn(
                                "flex gap-4",
                                isMobile
                                  ? "flex-col"
                                  : "items-center justify-between",
                              )}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-background">
                                  <AvatarImage
                                    src={conn.metadata?.avatar_url}
                                  />
                                  <AvatarFallback className="bg-muted">
                                    {conn.metadata?.username?.[0]?.toUpperCase() ||
                                      "G"}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  <p className="font-medium truncate">
                                    {conn.metadata?.username || conn.name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Connected{" "}
                                    {new Date(
                                      conn.createdAt,
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div
                                className={cn(isMobile && "flex justify-end")}
                              >
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() =>
                                    handleRemoveGitLabConnection(conn.id)
                                  }
                                  disabled={
                                    deletingGitlabConnectionId === conn.id
                                  }
                                  className="text-muted-foreground hover:text-destructive"
                                >
                                  {deletingGitlabConnectionId === conn.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-dashed border-2 border-border/50">
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
                          <Gitlab className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <h3 className="font-medium mb-1">
                          No GitLab accounts connected
                        </h3>
                        <p className="text-sm text-muted-foreground text-center mb-4">
                          Connect your GitLab account to sync projects with
                          repositories
                        </p>
                        <Button
                          variant="ghost"
                          onClick={handleConnectGitLab}
                          className="text-xs h-8 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1.5" />
                          Connect GitLab Account
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {activeTab === "theme-library" && (
                <div className="space-y-6">
                  {/* Title + description now live in the shell PageBar (see
                      usePageBar above) — no in-content header to avoid a
                      duplicate "Theme Library" heading. */}
                  <ThemeLibrary />
                </div>
              )}

              {activeTab === "api-keys" && (
                <div className="space-y-6">
                  {/* Title + description now live in the shell PageBar (see
                      usePageBar above) — no in-content header to avoid a
                      duplicate "API Keys" heading. */}

                  {/* Security Warning */}
                  <Card className="border-0 shadow-sm bg-muted/50">
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            Security reminder
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Keep your API keys secure. Never share them publicly
                            or commit them to version control.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {!isAdminOrOwner ? (
                    <Card className="border-0 shadow-sm bg-muted/50">
                      <CardContent className="p-4">
                        <div className="flex gap-3">
                          <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <Lock className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              Admin access required
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Contact your workspace administrator to manage API
                              keys.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ) : loadingKeys ? (
                    <div className="flex justify-center items-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div
                      className={cn(
                        "grid gap-6",
                        isMobile ? "grid-cols-1" : "grid-cols-2",
                      )}
                    >
                      {/* Content Signing Keys Section */}
                      <div className="space-y-4">
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                          Content Signing Keys
                        </h3>

                        <Card className="border-0 shadow-sm bg-muted/30">
                          <CardContent className="p-5">
                            <div className="flex items-start gap-3 mb-4">
                              <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                                <Key className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium">
                                  HMAC Authentication
                                </h4>
                                <p className="text-sm text-muted-foreground">
                                  Secure content delivery for published projects
                                </p>
                              </div>
                            </div>

                            {workspaceKeys.filter(
                              (k) => k.keyType === WorkspaceKeyType.CONTENT,
                            ).length === 0 ? (
                              <div className="flex flex-col items-center justify-center py-6 border-2 border-dashed border-border/50 rounded-lg">
                                <Key className="h-8 w-8 text-muted-foreground mb-2" />
                                <p className="text-sm text-muted-foreground text-center mb-3">
                                  No key generated yet
                                </p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setGenerateModal({
                                      open: true,
                                      keyType: WorkspaceKeyType.CONTENT,
                                    })
                                  }
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Generate Key
                                </Button>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {workspaceKeys
                                  .filter(
                                    (k) =>
                                      k.keyType === WorkspaceKeyType.CONTENT,
                                  )
                                  .map((key) => (
                                    <WorkspaceKeyCard
                                      key={key.keyId}
                                      keyData={key}
                                      onRotate={(
                                        keyId: string,
                                        version: number,
                                      ) =>
                                        setRotateModal({
                                          open: true,
                                          keyId,
                                          version,
                                        })
                                      }
                                      onRevoke={(keyId: string) =>
                                        setRevokeModal({ open: true, keyId })
                                      }
                                      isAdmin={isAdminOrOwner}
                                    />
                                  ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>

                      {/* Connection Keys Section */}
                      <div className="space-y-4">
                        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                          Connection Keys
                        </h3>

                        <Card className="border-0 shadow-sm bg-muted/30">
                          <CardContent className="p-5">
                            <div className="flex items-start gap-3 mb-4">
                              <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                                <Link2 className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium">Edge Functions</h4>
                                <p className="text-sm text-muted-foreground">
                                  Secure access to connection credentials
                                </p>
                              </div>
                            </div>

                            {workspaceKeys.filter(
                              (k) => k.keyType === WorkspaceKeyType.CONNECTION,
                            ).length === 0 ? (
                              <div className="flex flex-col items-center justify-center py-6 border-2 border-dashed border-border/50 rounded-lg">
                                <Link2 className="h-8 w-8 text-muted-foreground mb-2" />
                                <p className="text-sm text-muted-foreground text-center mb-3">
                                  No key generated yet
                                </p>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setGenerateModal({
                                      open: true,
                                      keyType: WorkspaceKeyType.CONNECTION,
                                    })
                                  }
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Generate Key
                                </Button>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {workspaceKeys
                                  .filter(
                                    (k) =>
                                      k.keyType === WorkspaceKeyType.CONNECTION,
                                  )
                                  .map((key) => (
                                    <WorkspaceKeyCard
                                      key={key.keyId}
                                      keyData={key}
                                      onRotate={(
                                        keyId: string,
                                        version: number,
                                      ) =>
                                        setRotateModal({
                                          open: true,
                                          keyId,
                                          version,
                                        })
                                      }
                                      onRevoke={(keyId: string) =>
                                        setRevokeModal({ open: true, keyId })
                                      }
                                      isAdmin={isAdminOrOwner}
                                    />
                                  ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  )}

                  {/* Modals */}
                  <GenerateKeyModal
                    key={generateModal.keyType}
                    isOpen={generateModal.open}
                    onClose={() =>
                      setGenerateModal({
                        open: false,
                        keyType: WorkspaceKeyType.CONTENT,
                      })
                    }
                    onSuccess={loadWorkspaceKeys}
                    keyType={generateModal.keyType}
                  />
                  <RotateKeyModal
                    isOpen={rotateModal.open}
                    onClose={() => setRotateModal({ open: false })}
                    keyId={rotateModal.keyId || ""}
                    currentVersion={rotateModal.version || 1}
                    onSuccess={loadWorkspaceKeys}
                  />
                  <RevokeKeyModal
                    isOpen={revokeModal.open}
                    onClose={() => setRevokeModal({ open: false })}
                    keyId={revokeModal.keyId || ""}
                    onSuccess={loadWorkspaceKeys}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
